import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    makeStyles,
    Tab,
    Tabs
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { Box, DialogContent, Grid, MenuItem, Select, Switch, TextField, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { i18n } from "../../../translate/i18n";
import integrationNames from "./integrationNames.json";
import integrationMethods from "./integrationMethods.json";
import api from "../../../services/api";
import { toast } from "react-toastify";
import { withStyles } from '@material-ui/core/styles';
import IntegrationHeaderTab from "../Tabs/Headers";
import * as Yup from "yup";
import toastError from "../../../errors/toastError";
import CheckSupportEmail from "../../../helpers/CheckSupportEmail";
import { AuthContext } from "../../../context/Auth/AuthContext";
import ApiConnectionQueueConfiguration, {
    areConnectionQueueSelectionsEqual,
    isConnectedConnection,
} from "./ApiConnectionQueueConfiguration";

const useStyles = makeStyles((theme) => ({
    root: {
        flexWrap: "wrap",
        maxWidth: '500px'
    },
    textField: {
        marginRight: theme.spacing(1),
        width: "100%",
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    textTagContainer: {
        width: "100%",
    },
    tabs: {
        background: 'transparent',
        color: '#000'
    }
}));

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`interation-tabpanel-${index}`}
            aria-labelledby={`interations-${index}`}
            component="div"
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </Box>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

function a11yProps(index) {
    return {
        id: `interation-tab-${index}`,
        'aria-controls': `interation-tabpanel-${index}`,
    };
}

const ApiModal = ({ open, onClose, integrationId }) => {
    const classes = useStyles();
    const [submittingContent, setSubmittingContent] = useState(false);
    const [connections, setConnections] = useState([]);
    const [connectionQueueSelecteds, setConnectionQueueSelecteds] = useState([]);
    const [value, setValue] = useState(0);
    const { user } = useContext(AuthContext);

    const initialState = {
        id: null,
        title: '',
        name: integrationNames['api'][0],
        url: '',
        method: integrationMethods['api'][0],
        body: 'JSON',
        type: 'api',
        auth: { option: 'NOAUTH', user: '', password: '' },
        headers: [
            { key: "Access-Control-Allow-Origin", value: "*" },
            { key: "Access-Control-Allow-Headers", value: "Authorization" },
        ],
        fields: []
    };
    const [integration, setIntegration] = useState(initialState);

    const isMessageIntegration = (name) => {
        return name === 'SEND_MESSAGES' || name === 'SEND_AI_MESSAGES';
    };

    const setFieldValue = (key, value) => {
        let fields = Array.isArray(integration?.fields) ? [...integration.fields] : [];
        const idx = fields.findIndex((field) => field.key === key);
        const obj = { key, value };
        if (idx >= 0) fields[idx] = obj;
        else fields.push(obj);
        setIntegration({ ...integration, fields });
    };

    const getFieldValue = (key, fallback = "") => {
        const field = integration?.fields?.find((f) => f.key === key);
        return field?.value ?? fallback;
    };

    const getActiveConnectionIdsFromFields = (fieldsList) => {
        const fields = Array.isArray(fieldsList) ? fieldsList : [];
        const active = fields.find((f) => f.key === "activeConnectionIds")?.value;
        if (Array.isArray(active) && active.length) {
            return active.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
        }
        const primary = fields.find((f) => f.key === "connectionId")?.value;
        return primary ? [Number(primary)] : [];
    };

    const getActiveConnectionIds = () =>
        getActiveConnectionIdsFromFields(integration?.fields);

    const getConnectionQueueSelectedsFromFields = (fieldsList) => {
        const fields = Array.isArray(fieldsList) ? fieldsList : [];
        const mappings = fields.find((f) => f.key === "connectionQueueMappings")?.value;

        if (Array.isArray(mappings) && mappings.length > 0) {
            return mappings.map((item) => ({
                connectionId: Number(item.connectionId),
                queueIds: item.queueId ? [Number(item.queueId)] : [],
            }));
        }

        const activeIds = getActiveConnectionIdsFromFields(fields);
        return activeIds.map((connectionId) => ({
            connectionId,
            queueIds: [],
        }));
    };

    const filterSelectableConnectionQueues = (selecteds, conns = connections) =>
        (Array.isArray(selecteds) ? selecteds : []).filter((item) => {
            const conn = (Array.isArray(conns) ? conns : []).find(
                (c) => Number(c.id) === Number(item.connectionId)
            );
            return conn && isConnectedConnection(conn);
        });

    const applyConnectionQueueSelection = useCallback((selecteds, connsOverride) => {
        const list = filterSelectableConnectionQueues(
            selecteds,
            connsOverride ?? connections
        );

        setConnectionQueueSelecteds((prev) =>
            areConnectionQueueSelectionsEqual(prev, list) ? prev : list
        );

        const activeIds = list
            .map((item) => Number(item.connectionId))
            .filter((id) => !Number.isNaN(id) && id > 0);

        const mappings = list.map((item) => ({
            connectionId: Number(item.connectionId),
            queueId: item.queueIds?.[0] ? Number(item.queueIds[0]) : null,
        }));

        setIntegration((prev) => {
            const fields = Array.isArray(prev?.fields) ? [...prev.fields] : [];
            const currentActive =
                fields.find((f) => f.key === "activeConnectionIds")?.value || [];
            const currentMappings =
                fields.find((f) => f.key === "connectionQueueMappings")?.value || [];
            const currentPrimary = fields.find((f) => f.key === "connectionId")?.value;

            const sameActive =
                JSON.stringify(currentActive) === JSON.stringify(activeIds);
            const sameMappings =
                JSON.stringify(currentMappings) === JSON.stringify(mappings);
            const samePrimary = currentPrimary === (activeIds[0] || null);

            if (sameActive && sameMappings && samePrimary) return prev;

            const patch = (key, value) => {
                const idx = fields.findIndex((f) => f.key === key);
                const entry = { key, value };
                if (idx >= 0) fields[idx] = entry;
                else fields.push(entry);
            };
            patch("activeConnectionIds", activeIds);
            patch("connectionQueueMappings", mappings);
            patch("connectionId", activeIds[0] || null);
            return { ...prev, fields };
        });
    }, [connections]);

    const hasSavedConnectionQueueConfig = (fieldsList) =>
        getActiveConnectionIdsFromFields(fieldsList).length > 0;

    const hydrateConnectionQueueFromFields = (fieldsList, conns) => {
        const selecteds = getConnectionQueueSelectedsFromFields(fieldsList);
        const list = (Array.isArray(selecteds) ? selecteds : []).filter((item) => {
            const conn = (Array.isArray(conns) ? conns : []).find(
                (c) => Number(c.id) === Number(item.connectionId)
            );
            return !!conn;
        });
        setConnectionQueueSelecteds((prev) =>
            areConnectionQueueSelectionsEqual(prev, list) ? prev : list
        );
    };

    const normalizeIntegrationFields = (data) => {
        if (!data) return data;
        let rawFields = data.fields;
        if (typeof rawFields === "string") {
            try {
                rawFields = JSON.parse(rawFields);
            } catch {
                rawFields = [];
            }
        }
        if (!Array.isArray(rawFields)) return { ...data, fields: [] };
        const fields = [...rawFields];
        const connectionId = fields.find((f) => f.key === "connectionId")?.value;
        const activeIdx = fields.findIndex((f) => f.key === "activeConnectionIds");
        let activeIds = activeIdx >= 0 ? fields[activeIdx]?.value : null;

        if (!Array.isArray(activeIds) || !activeIds.length) {
            activeIds = connectionId ? [Number(connectionId)] : [];
            const entry = { key: "activeConnectionIds", value: activeIds };
            if (activeIdx >= 0) fields[activeIdx] = entry;
            else fields.push(entry);
        }

        if (activeIds.length) {
            const connIdx = fields.findIndex((f) => f.key === "connectionId");
            const connEntry = { key: "connectionId", value: activeIds[0] };
            if (connIdx >= 0) fields[connIdx] = connEntry;
            else fields.push(connEntry);
        }

        const mappingsIdx = fields.findIndex((f) => f.key === "connectionQueueMappings");
        let mappings = mappingsIdx >= 0 ? fields[mappingsIdx]?.value : null;
        if (!Array.isArray(mappings) || !mappings.length) {
            const defaultQueueId = fields.find((f) => f.key === "defaultQueueId")?.value;
            mappings = activeIds.map((connectionId) => ({
                connectionId,
                queueId: defaultQueueId ? Number(defaultQueueId) : null,
            }));
            const entry = { key: "connectionQueueMappings", value: mappings };
            if (mappingsIdx >= 0) fields[mappingsIdx] = entry;
            else fields.push(entry);
        }

        return { ...data, fields };
    };

    const isWhatsappModule = (c) => {
        const moduleId = Number(c?.moduleId);
        return !moduleId || moduleId === 1 || moduleId === 4;
    };

    const isMassiveConnection = (c) =>
        String(c?.option || "").toLowerCase() === "massive";

    const isActiveConnection = (c) => Number(c?.state) === 1;

    const filterMassiveActiveConnections = (list) => {
        if (!Array.isArray(list)) return [];
        return list.filter(
            (c) => isWhatsappModule(c) && isMassiveConnection(c) && isActiveConnection(c)
        );
    };

    const massiveConnections = useMemo(() => {
        const base = filterMassiveActiveConnections(connections);
        const savedIds = getActiveConnectionIdsFromFields(integration?.fields);
        const merged = [...base];
        savedIds.forEach((id) => {
            if (merged.some((c) => Number(c.id) === id)) return;
            const conn = connections.find((c) => Number(c.id) === id);
            if (conn && isWhatsappModule(conn) && isMassiveConnection(conn)) {
                merged.push(conn);
            }
        });
        return merged;
    }, [connections, integration?.fields]);

    const syncSingleMassiveConnection = (massiveList, connsOverride) => {
        if (massiveList.length !== 1) return;
        const id = Number(massiveList[0].id);
        applyConnectionQueueSelection(
            [{ connectionId: id, queueIds: [] }],
            connsOverride
        );
    };

    useEffect(() => {
        if (open == false) return;

        let isMounted = true;
        const abortController = new AbortController();
        
        // Função para recuperar suas conexões
        (async () => {
            try {
                const connResp = await api.get('/whatsappListAll', {
                    params: { disabled: '1' },
                    signal: abortController.signal,
                });
                if (!isMounted) return;

                if (!connResp || connResp.status !== 200) throw new Error();

                const loadedConnections = Array.isArray(connResp.data) ? connResp.data : [];
                setConnections(loadedConnections);

                const massiveOnly = filterMassiveActiveConnections(loadedConnections);

                if (integrationId) {
                    const { data: integrationData } = await api.get(`/integrations/${integrationId}`, {
                        signal: abortController.signal,
                    });
                    if (!isMounted) return;
                    const normalized = normalizeIntegrationFields(integrationData);
                    setIntegration(normalized);
                    hydrateConnectionQueueFromFields(
                        normalized.fields,
                        loadedConnections
                    );
                    if (
                        massiveOnly.length === 1 &&
                        isMessageIntegration(normalized.name) &&
                        !hasSavedConnectionQueueConfig(normalized.fields)
                    ) {
                        syncSingleMassiveConnection(massiveOnly, loadedConnections);
                    }
                } else if (
                    massiveOnly.length >= 1 &&
                    isMessageIntegration(integrationNames["api"][0])
                ) {
                    if (!isMounted) return;
                    if (massiveOnly.length === 1) {
                        syncSingleMassiveConnection(massiveOnly, loadedConnections);
                    } else if (getActiveConnectionIdsFromFields(integration?.fields).length === 0) {
                        const autoSelecteds = massiveOnly
                            .filter((c) => isConnectedConnection(c))
                            .map((c) => ({
                                connectionId: Number(c.id),
                                queueIds: [],
                            }));
                        if (autoSelecteds.length) {
                            applyConnectionQueueSelection(autoSelecteds, loadedConnections);
                        }
                    }
                }
            } catch (error) {
                if (!isMounted || error.name == 'AbortError') return;
                console.error({ err: error });
                toast.error('Erro ao resgatar conexões, contate o administrador.');
            }
        })();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [open]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    // Handlers
    const handleClose = () => {
        setIntegration(initialState);
        setConnections([]);
        setConnectionQueueSelecteds([]);
        setValue(0);
        onClose();
    };

    const handleSave = async () => {
        // validações
        setSubmittingContent(true);

        // validações
        let validate = false;
        try {
            const validateData = {
                title: integration.title,
                name: integration.name,
                method: integration.method,
            };
            await CheckApiSchema.validate(validateData);
            validate = true;

            const payload = normalizeIntegrationFields(integration);

            if (!integrationId) {
                await api.post("/integrations", payload);
            } else {
                await api.put(`/integrations/${integrationId}`, payload);
            }

            toast.success(i18n.t(`integrations.api.form.validations.success`));
            handleClose();
        } catch (err) {
            console.error({ err });
            if (validate == false) toastError({ response: { data: { error: err?.message || 'INTERNAL_ERROR' } } });
            else {
                toastError(err);
            }
        } finally {
            setTimeout(() => {
                setSubmittingContent(false);
            }, 500);
        }
    }

    // gravar escolha do reenvio de respostas
    const handleSendContactAnswer = (status) => {
        let fields = integration?.fields;
        if (!fields || !Array.isArray(fields)) return;
        const idx = fields.findIndex(field => field.key == 'sendContactAnswer');
        const obj = { key: 'sendContactAnswer', value: status };

        if (idx >= 0) fields[idx] = obj;
        else fields.push(obj);

        setIntegration({ ...integration, fields });
    }

    // gravar url de reenvio
    const handleSendContactAnswerUrl = (url) => {
        let fields = integration?.fields;
        if (!fields || !Array.isArray(fields)) return;
        const idx = fields.findIndex(field => field.key == 'sendContactAnswerUrl');
        const obj = { key: 'sendContactAnswerUrl', value: url };

        if (idx >= 0) fields[idx] = obj;
        else fields.push(obj);

        setIntegration({ ...integration, fields });
    }

    // gravar mensagem de resposta ao contato
    const handleAnswerContactMessage = (url) => {
        let fields = integration?.fields;
        if (!fields || !Array.isArray(fields)) return;
        const idx = fields.findIndex(field => field.key == 'answerContactMessage');
        const obj = { key: 'answerContactMessage', value: url };

        if (idx >= 0) fields[idx] = obj;
        else fields.push(obj);

        setIntegration({ ...integration, fields });
    }

    // Switch customizado
    const AntSwitch = withStyles((theme) => ({
        root: {
            width: 28,
            height: 16,
            padding: 0,
            display: 'flex',
        },
        switchBase: {
            padding: 2,
            color: theme.palette.grey[500],
            '&$checked': {
                transform: 'translateX(12px)',
                color: theme.palette.common.white,
                '& + $track': {
                    opacity: 1,
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                },
            },
        },
        thumb: {
            width: 12,
            height: 12,
            boxShadow: 'none',
        },
        track: {
            border: `1px solid ${theme.palette.grey[500]}`,
            borderRadius: 16 / 2,
            opacity: 1,
            backgroundColor: theme.palette.common.white,
        },
        checked: {},
    }))(Switch);

    // validação de campos
    const CheckApiSchema = Yup.object().shape({
        title: Yup.string()
            .required(i18n.t(`integrations.api.form.validations.titleRequired`))
            .min(5, i18n.t(`integrations.api.form.validations.titleMin`)),
        name: Yup.string()
            .required(i18n.t(`integrations.api.form.validations.nameRequired`))
            .min(5, i18n.t(`integrations.api.form.validations.nameMin`)),
        method: Yup.string()
            .required(i18n.t(`integrations.api.form.validations.methodRequired`))
            .min(2, i18n.t(`integrations.api.form.validations.methodMin`)),
        activeConnections: Yup.mixed().test(
            "check-activeConnections",
            i18n.t(`integrations.api.form.validations.activeConnectionsRequired`),
            async () => {
                if (!isMessageIntegration(integration.name)) return true;
                return getActiveConnectionIds().length > 0;
            }
        ),
        url: Yup.string()
            .test(
                "check-url",
                i18n.t(`integrations.api.form.validations.urlRequired`),
                async value => {
                    if (!isMessageIntegration(integration.name)) return true;
                    const sendContactAnswer = integration?.fields?.find(field => field.key == 'sendContactAnswer')?.value == true ? true : false;
                    if (!sendContactAnswer) return true;
                    const url = integration?.fields?.find(field => field.key == 'sendContactAnswerUrl')?.value || '';
                    return !url || url == '' ? false : true;
                }
            ),
    });
    // reordenar para seguir sequencia correta
    if (CheckApiSchema['_nodes']) CheckApiSchema['_nodes'] = CheckApiSchema['_nodes'].reverse();

    // Renderização
    return (
      <div className={classes.root}>
        <Dialog
          open={open == true ? true : false}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          scroll="paper"
          disableEscapeKeyDown={true}
        >
          <DialogTitle id="form-dialog-title" style={{ paddingBottom: 0 }}>
            {!integrationId
              ? i18n.t(`integrations.api.form.create`)
              : i18n.t(`integrations.api.form.update`)}
          </DialogTitle>
          <Formik
            initialValues={integration}
            enableReinitialize={true}
            onSubmit={(values, actions) => {
              handleSave();
            }}
          >
            {({ values, errors, touched }) => (
              <Form style={{ padding: "10px" }}>
                <DialogContent>
                  <Field
                    as={TextField}
                    type="hidden"
                    name="adminId"
                    hidden={true}
                  />

                  {/* nome e método */}
                  <Grid container spacing={2}>
                    <Grid item xs={8} mb={2}>
                      <Typography component="label">
                        {i18n.t(`integrations.api.form.name`)}
                        <Typography
                          component="span"
                          className="text-danger ml-2"
                        >
                          *
                        </Typography>
                      </Typography>
                      <Field
                        style={{
                          height: "45px",
                        }}
                        className="form-control"
                        variant="outlined"
                        as={Select}
                        value={integration.name || ""}
                        onChange={(e) =>
                          setIntegration({
                            ...integration,
                            name: e.target.value,
                          })
                        }
                      >
                        {Array.isArray(integrationNames["api"]) &&
                          integrationNames["api"].map((name, index) => {
                            if (
                              name === "SEND_AI_MESSAGES" &&
                              !CheckSupportEmail(user.email)
                            ) {
                              return null;
                            }
                            return (
                              <MenuItem value={name} key={index}>
                                {name}
                              </MenuItem>
                            );
                          })}
                      </Field>
                    </Grid>
                    <Grid item xs={4} mb={2}>
                      <Typography component="label">
                        {i18n.t(`integrations.api.form.method`)}
                        <Typography
                          component="span"
                          className="text-danger ml-2"
                        >
                          *
                        </Typography>
                      </Typography>
                      <Field
                        style={{
                          height: "45px",
                        }}
                        className="form-control"
                        variant="outlined"
                        as={Select}
                        disabled={isMessageIntegration(integration.name)}
                        value={integration.method || ""}
                        onChange={(e) =>
                          setIntegration({
                            ...integration,
                            method: e.target.value,
                          })
                        }
                      >
                        {Array.isArray(integrationMethods["api"]) &&
                          integrationMethods["api"].map((m, index) => (
                            <MenuItem value={m} key={index}>
                              {m}
                            </MenuItem>
                          ))}
                      </Field>
                    </Grid>
                  </Grid>

                  {/* titulo */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} mb={2}>
                      <Typography component="label">
                        {i18n.t(`integrations.api.form.title`)}
                        <Typography
                          component="span"
                          className="text-danger ml-2"
                        >
                          *
                        </Typography>
                      </Typography>
                      <Field
                        style={{
                          borderColor: "#c4c4c4",
                          borderRadius: "3px",
                          resize: "none",
                        }}
                        className="form-control"
                        placeholder={i18n.t(`integrations.api.form.title`)}
                        maxLength={100}
                        value={integration.title || ""}
                        onChange={(e) =>
                          setIntegration({
                            ...integration,
                            title: e.target.value,
                          })
                        }
                      />
                    </Grid>
                  </Grid>

                  {isMessageIntegration(integration.name) && (
                    <>
                      {/* conexões e filas (roleta) */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} mb={2}>
                          <Typography component="label">
                            {i18n.t(`integrations.api.form.connectionsAndQueuesTitle`)}
                            <Typography
                              component="span"
                              className="text-danger ml-2"
                            >
                              *
                            </Typography>
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginBottom: 12, whiteSpace: "pre-line" }}
                          >
                            {i18n.t(`integrations.api.form.connectionsAndQueuesDescription`)}
                          </Typography>
                          <ApiConnectionQueueConfiguration
                            connections={massiveConnections}
                            selecteds={connectionQueueSelecteds}
                            onSelected={(sel) =>
                              applyConnectionQueueSelection(sel, connections)
                            }
                            isCollapsed={true}
                          />
                        </Grid>
                      </Grid>

                      {/* status padrão do atendimento */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} mb={2}>
                          <Typography component="label">
                            {i18n.t(`integrations.api.form.defaultTicketStatus`)}
                          </Typography>
                          <Field
                            style={{ height: "45px" }}
                            className="form-control"
                            variant="outlined"
                            as={Select}
                            value={getFieldValue("defaultTicketStatus", "closed")}
                            onChange={(e) =>
                              setFieldValue("defaultTicketStatus", e.target.value)
                            }
                          >
                            <MenuItem value="closed">
                              {i18n.t(`integrations.api.form.ticketStatusClosed`)}
                            </MenuItem>
                            <MenuItem value="open">
                              {i18n.t(`integrations.api.form.ticketStatusOpen`)}
                            </MenuItem>
                          </Field>
                        </Grid>
                      </Grid>

                      {/* resposta padrão para quando o usuário responder */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} mb={2}>
                          <Typography component="label">
                            {i18n.t(`integrations.api.form.answerContact`)}
                          </Typography>
                          <Field
                            style={{
                              borderColor: "#c4c4c4",
                              borderRadius: "3px",
                              resize: "none",
                            }}
                            className="form-control"
                            placeholder={i18n.t(
                              `integrations.api.form.answerContactPlaceholder`
                            )}
                            maxLength={255}
                            value={
                              integration?.fields?.find(
                                (field) => field.key == "answerContactMessage"
                              )?.value || ""
                            }
                            onChange={(e) =>
                              handleAnswerContactMessage(e.target.value)
                            }
                            component="textarea"
                            resize={false}
                          />
                        </Grid>
                      </Grid>

                      {/* enviar resposta do contato */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} mb={2}>
                          <Grid item style={{ display: "inline-block" }}>
                            <AntSwitch
                              checked={
                                integration?.fields?.find(
                                  (field) => field.key == "sendContactAnswer"
                                )?.value == true || false
                              }
                              onChange={(e) =>
                                handleSendContactAnswer(
                                  e.target.checked ? true : false
                                )
                              }
                              name="sendContactAnswer"
                            />
                          </Grid>
                          <Grid item style={{ display: "inline-block" }}>
                            {i18n.t(`integrations.api.form.sendContactAnswer`)}
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* url para retornar a resposta do contato */}
                      <Grid
                        container
                        spacing={2}
                        hidden={
                          integration?.fields?.find(
                            (field) => field.key == "sendContactAnswer"
                          )?.value != true
                        }
                      >
                        <Grid item xs={12} mb={2}>
                          <Typography component="label">
                            {i18n.t(`integrations.api.form.url`)}
                            <Typography
                              component="span"
                              className="text-danger ml-2"
                            >
                              *
                            </Typography>
                          </Typography>
                          <Field
                            style={{
                              borderColor: "#c4c4c4",
                              borderRadius: "3px",
                            }}
                            className="form-control"
                            placeholder={i18n.t(`integrations.api.form.url`)}
                            maxLength={255}
                            value={
                              integration?.fields?.find(
                                (field) => field.key == "sendContactAnswerUrl"
                              )?.value || ""
                            }
                            onChange={(e) =>
                              handleSendContactAnswerUrl(e.target.value)
                            }
                          />
                        </Grid>
                      </Grid>

                      {/* cabeçalhos e campos extras */}
                      <Grid
                        container
                        spacing={2}
                        hidden={
                          integration?.fields?.find(
                            (field) => field.key == "sendContactAnswer"
                          )?.value != true
                        }
                      >
                        <Grid item xs={12} mb={2}>
                          <Box
                            component="div"
                            style={{ height: "350px", overflowY: "auto" }}
                          >
                            <Tabs
                              value={value}
                              onChange={handleChange}
                              aria-label="simple tabs example"
                            >
                              <Tab
                                label={i18n.t(`integrations.api.form.headers`)}
                                {...a11yProps(0)}
                              />
                            </Tabs>
                            <TabPanel value={value} index={0}>
                              <IntegrationHeaderTab
                                integrationData={integration}
                                setIntegrationData={setIntegration}
                              />
                            </TabPanel>
                          </Box>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={submittingContent}
                    variant="outlined"
                  >
                    {i18n.t(`integrations.api.buttons.cancel`)}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={submittingContent}
                    variant="contained"
                  >
                    {i18n.t(`integrations.api.buttons.save`)}
                    {submittingContent && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Dialog>
      </div>
    );
};

export default ApiModal;